import { useCallback, useEffect, useRef, useState } from 'react';
import { BidOutcome, OutcomeText, describeOutcome, followBid, pendingBidId } from '../../services/bidOutcome';

/**
 * The on-screen result of the last bid attempt on one lot. When the first answer was "not
 * confirmed yet" (no decision within 12s, or no reply to the write within 10s), it keeps following
 * that bid and REPLACES the notice with the real outcome the moment the server decides — so a
 * bidder never reads "we can't confirm this" while their accepted bid is listed below it.
 * Stops following on dismiss, on the next attempt, and on unmount.
 */
export const useBidOutcomeNotice = (auctionId: string, lotId: string) => {
  const [notice, setNotice] = useState<OutcomeText | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const stopFollowing = () => { stopRef.current?.(); stopRef.current = null; };

  useEffect(() => () => stopFollowing(), []);

  const clear = useCallback(() => { stopFollowing(); setNotice(null); }, []);

  const show = useCallback((o: BidOutcome, amountCents?: number, lotNumber?: string | number) => {
    stopFollowing();
    setNotice(describeOutcome(o, amountCents, lotNumber));
    const id = pendingBidId(o);
    if (id) {
      stopRef.current = followBid(auctionId, lotId, id, (later) => {
        stopRef.current = null;
        setNotice(describeOutcome(later, amountCents, lotNumber));
      });
    }
  }, [auctionId, lotId]);

  return { notice, show, clear };
};

export default useBidOutcomeNotice;
